import React, { useState } from 'react';
import { css } from '@emotion/react';
import { Gender } from '~/pages/CreateNewCharacter';
import { DefaultButton, FlexStack, Icon } from '~/components/atomic';
import { FullScreenLoading } from '~/components/common';
import { Header } from '~/components/common/Header';
import {
  useCreateNewAvaturnMutation,
  useGetAvaturnUserIdQuery,
} from '~/features/avaturn';
import { CreateNewAvaturnAvatarRequest } from '~/types';
import useCreateNewCharacterStore from '~/store/useCreateNewCharacterStore';
import useToast from '~/store/useToast';
import {
  BottomWrapper,
  Container,
} from '../SelectCharacter/SelectCharacter.styled';
import * as S from './CustomizeAvaturn.styled';
import UploadInput from './UploadInput/UploadInput';

const Required = [
  {
    iconName: 'User',
    description: 'Only accept photos of real people (not animation character)',
  },
] as const;

const Recommended = [
  {
    iconName: 'CameraOn',
    description: 'Long distance to face will cause distortion.',
  },
  {
    iconName: 'Canvas',
    description: 'Images should be vertical, 3:4 or 9:16.',
  },
  {
    iconName: 'Group',
    description: 'Please do not let your glasses or hair cover your face.',
  },
  {
    iconName: 'Sun',
    description: 'Good lighting in image leads to best results.',
  },
] as const;

interface CustomizeAvaturnProps {
  onPrevStep: () => void;
  onNextStep: () => void;
  gender: Gender;
  setGender: React.Dispatch<React.SetStateAction<Gender>>;
}

export default function CustomizeAvaturn({
  onPrevStep,
  onNextStep,
  gender,
  setGender,
}: CustomizeAvaturnProps) {
  const { showToast } = useToast();
  const setAvarturnState = useCreateNewCharacterStore(
    (s) => s.setAvarturnState,
  );
  const { data: avaturnId } = useGetAvaturnUserIdQuery();

  const [frontFaceImage1, setFrontFaceImage1] = useState<File>();

  const { mutate: createAvaturnMutate, isPending: createAvaturnPending } =
    useCreateNewAvaturnMutation();

  const disabled = !frontFaceImage1;

  const handleNextClick = () => {
    if (!disabled) {
      if (avaturnId) {
        const requestData: CreateNewAvaturnAvatarRequest = {
          gender: gender,
          user_id: avaturnId,
          image_frontal: frontFaceImage1,
        };
        createAvaturnMutate(requestData, {
          onSuccess({ status, data }) {
            if (!data.id || !data.url) {
              showToast(
                'error',
                'Failed to generate avatar. Please try again.',
              );
              setFrontFaceImage1(undefined);
              return;
            }
            setAvarturnState({
              id: data.id,
              url: data.url,
            });
            onNextStep();
            return;
          },
        });
      }
    }
  };

  const isPending = createAvaturnPending;

  return (
    <>
      {isPending && <FullScreenLoading />}
      <Header.Root>
        <Header.Section>
          <Header.PrevButton onPrevClick={onPrevStep} />
          <Header.Title>Generate avatar</Header.Title>
        </Header.Section>
      </Header.Root>

      <Container>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 24px;
          `}
        >
          <FlexStack spacing={16}>
            <S.Title>Upload image</S.Title>
            <FlexStack
              direction='row'
              css={css`
                width: 100%;
                height: 300px;
              `}
            >
              <UploadInput
                file={frontFaceImage1}
                setFile={setFrontFaceImage1}
                direction={'front'}
              />
            </FlexStack>
          </FlexStack>
          <FlexStack
            direction='row'
            css={css`
              align-items: center;
              justify-content: space-between;
              gap: 12px;
            `}
          >
            <S.Title>Select gender</S.Title>
            <S.GenderButtonWrapper>
              {(['male', 'female'] as Gender[]).map((el) => (
                <S.GenderButton
                  key={el}
                  onClick={() => setGender(el)}
                  className={el === gender ? 'on' : ''}
                >
                  {el}
                </S.GenderButton>
              ))}
            </S.GenderButtonWrapper>
          </FlexStack>

          <S.GuideContainer>
            <FlexStack>
              <S.SubTitle>Important</S.SubTitle>
              <S.GuideDescription>
                You’ll get best results if you upload an image that follows the
                guideline.
              </S.GuideDescription>
            </FlexStack>
            <FlexStack spacing={16}>
              <FlexStack>
                <S.GuideTitle>Required</S.GuideTitle>
                <FlexStack>
                  {Required.map((el, index) => (
                    <S.GuideWrapper key={index}>
                      <Icon iconName={el.iconName} />
                      <span>{el.description}</span>
                    </S.GuideWrapper>
                  ))}
                </FlexStack>
              </FlexStack>

              <FlexStack>
                <S.GuideTitle className='b'>Recommended</S.GuideTitle>
                <FlexStack spacing={16}>
                  {Recommended.map((el, index) => (
                    <S.GuideWrapper key={index}>
                      <Icon iconName={el.iconName} />
                      <span>{el.description}</span>
                    </S.GuideWrapper>
                  ))}
                </FlexStack>
              </FlexStack>
            </FlexStack>
          </S.GuideContainer>
        </div>

        <BottomWrapper
          css={css`
            margin-top: 16px;
          `}
        >
          <DefaultButton onClick={handleNextClick} disabled={disabled}>
            Next
            <Icon iconName='ArrowRight' />
          </DefaultButton>
        </BottomWrapper>
      </Container>
    </>
  );
}
